import React, {useEffect, useState} from 'react';
import {ArrowPathIcon, BanknotesIcon, PlusIcon} from '@heroicons/react/24/outline';
import {CreditCardIcon} from '@heroicons/react/20/solid';
import styles from './ClientBillingButtons.module.css';
import UpdatePaymentTypePopUp from "../../../views/client/popup/UpdatePaymentTypePopUp";
import {
    clientKeyAPI,
    deleteCardAPI,
    getCardAPI,
    getInfoForPayAPI,
    updatePaymentTypeAPI
} from "../../../services/client";
import {PaymentType} from "../../../types/contract";
import CreateWorkerRequestPopUp from "../../../views/client/popup/CreateWorkerRequestPopUp";
import {loadTossPayments, TossPaymentsPayment} from "@tosspayments/tosspayments-sdk";
import {useAlert} from "../../common/error/AlertContext";
import {CardInfoResponseData} from "../../../types/client";
import TextCard from "../../common/textCard/TextCard";
import CreateTaxInvoicePopUp from "../../../views/client/popup/CreateTaxInvoicePopUp";

interface ClientBillingButtonsProps {
    paymentType?: string;
    payday: string;
    amount: number;
    onClose: () => void;
}

// 결제 기능 버튼
const ClientBillingButtons = ({paymentType, payday, amount, onClose}: ClientBillingButtonsProps) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isWorkerOpen, setIsWorkerOpen] = useState(false);
    const [isTaxInvoiceOpen, setIsTaxInvoiceOpen] = useState(false);
    const [payment, setPayment] = useState<TossPaymentsPayment | null>(null);
    const [card, setCard] = useState<CardInfoResponseData | null>(null);
    const {showAlert} = useAlert();

    // 토스페이먼츠 SDK 사용 위한 키
    const clientKey = process.env.REACT_APP_TOSS_CLIENT_KEY || "";

    // 토스페이멑츠 SDK 초기화
    const initTossPayments = async () => {
        try {
            // 클라이언트 키 조회 API, 토스페이먼츠의 클라이언트 식별용
            const customerKey = await clientKeyAPI();
            // 토스페이먼츠 SDK 사용
            const tossPayments = await loadTossPayments(clientKey);
            const paymentInstance = tossPayments.payment({
                customerKey,
            });
            setPayment(paymentInstance);
        } catch (error) {
            console.error("토스페이먼츠 초기화 실패:", error);
        }
    };

    const fetchCard = async () => {
        try {
            //클라이언트 정기 결제 카드 조회
            const response = await getCardAPI();
            setCard(response);
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        initTossPayments();
        fetchCard();
    }, [clientKey]);

    // 정기결제 카드 정보
    const getCardInfo = () => {
        if (!card) return [];

        return [
            {
                label: "카드사",
                value: card.company || "-"
            },
            {
                label: "카드번호",
                value: card.cardNumber || "-"
            },
            {
                label: "카드종류",
                value: card.cardType || "-"
            },
            {
                label: "카드소유",
                value: card.ownerType || "-"
            }
        ];
    };

    //정기결제 카드 삭제
    const deleteCard = async () => {
        // showAlert('개발 중인 기능입니다.', "info");
        try {
            // 정기결제 카드 삭제 API
            await deleteCardAPI();
            showAlert('등록된 카드가 삭제되었습니다.', "info");
            await fetchCard();
        } catch (error) {
            console.error(error);
        }
    }

    // 카드 결제
    const requestCardPayment = async () => {
        // showAlert('개발 중인 기능입니다.', "info");
        try {
            if (!payment) {
                console.error("결제 인스턴스가 초기화되지 않았습니다.");
                return;
            }

            // 토스페이먼츠용 주문 식별자
            const orderId = `order_${Date.now()}`;
            //결제에 필요한 이메일, 회사명 조회
            const response = await getInfoForPayAPI();
            // 토스페이먼츠 카드 결제, 카드결제/간편결제 중 선택
            await payment.requestPayment({
                method: "CARD",
                amount: {
                    currency: "KRW",
                    value: amount,
                },
                orderId: orderId,
                orderName: payday + " 서비스 이용료",
                successUrl: `${window.location.origin}/client/billing/success`, //결제 성공 시 이동 경로
                failUrl: `${window.location.origin}/client/billing/fail`, //결제 실패 시 이동 경로
                customerEmail: response.email, //클라이언트 이메일
                customerName: response.name, //클라이언트 회사명
                card: {
                    useEscrow: false,
                    flowMode: "DEFAULT",
                    useCardPoint: false,
                    useAppCardOnly: false,
                }
            });
        } catch (error) {
            console.error("결제 요청 실패:", error);
        }
    };

    // 계좌이체 결제
    const requestAccountPayment = async () => {
        // showAlert('개발 중인 기능입니다.', "info");
        try {
            if (!payment) {
                console.error("결제 인스턴스가 초기화되지 않았습니다.");
                return;
            }
            // 토스페이먼츠용 주문 식별자
            const orderId = `order_${Date.now()}`;
            //결제에 필요한 이메일, 회사명 조회
            const response = await getInfoForPayAPI();
            // 토스 페이먼츠 계좌이체 결제
            await payment.requestPayment({
                method: "TRANSFER",
                amount: {
                    currency: "KRW",
                    value: amount,
                },
                orderId: orderId,
                orderName: payday + " 서비스 이용료",
                successUrl: `${window.location.origin}/client/billing/success`, //결제 성공 시 이동 경로
                failUrl: `${window.location.origin}/client/billing/fail`, //결제 실패 시 이동 경로
                customerEmail: response.email, //클라이언트 이메일
                customerName: response.name, //클라이언트 회사명
                transfer: {
                    cashReceipt: {
                        type: "소득공제"
                    },
                    useEscrow: false,
                }
            });
        } catch (error) {
            console.error("결제 요청 실패:", error);
        }
    };

    //결제 방법 수정, 카드/계좌이체
    const changePaymentType = async () => {
        try {
            const type = paymentType === PaymentType.신용카드 ? PaymentType.계좌이체 : PaymentType.신용카드;
            //결제방법 수정 API
            await updatePaymentTypeAPI(type);
            onClose();
        } catch (error) {
            console.log(error);
        }
    }

    const registerPaymentType = () => {
        setIsModalOpen(true);
    }

    //작업자 추가 요청 생성 팝업
    const addWorker = () => {
        setIsWorkerOpen(true);
    }

    const openTaxPopUp = () => {
        setIsTaxInvoiceOpen(true);
    }

    //정기결제 카드 등록
    const registerBilling = async () => {
        // showAlert('개발 중인 기능입니다.', "info");
        try {
            if (!payment) {
                console.error("결제 인스턴스가 초기화되지 않았습니다.");
                return;
            }
            //등록에 필요한 이메일, 회사명 조회 API
            const response = await getInfoForPayAPI();
            await payment.requestBillingAuth({
                method: 'CARD',
                successUrl: window.location.origin + '/client/billing/register', //성공 시 이동 경로
                failUrl: window.location.origin + '/fail', //실패 시 이동 경로
                customerEmail: response.email,
                customerName: response.name,
            });
        } catch (error) {
            console.error("결제 요청 실패:", error);
        }
    }

    const handleCloseModal = () => {
        setIsModalOpen(false);
        onClose();
    }

    const handleCloseWorkerModal = () => {
        setIsWorkerOpen(false);
    }

    const closeTaxPopUp = () => {
        setIsTaxInvoiceOpen(false);
    }

    const getButtons = () => {
        //결제 방법 등록 안 한 경우
        const commonButtons = [
            //결제방법 등록, 신용카드/계좌이체
            {
                icon: <CreditCardIcon className={styles.icon}/>,
                label: '결제방법 등록',
                onClick: registerPaymentType
            },
            //작업자 추가 요청
            {
                icon: <PlusIcon className={styles.icon}/>,
                label: '작업자 추가',
                onClick: addWorker
            }
        ];

        if (!paymentType) {
            return commonButtons;
        }
        //결제 방법이 계좌이체인 경우
        if (paymentType === "계좌이체") {
            return [
                //계좌이체 결제
                {
                    icon: <BanknotesIcon className={styles.icon}/>,
                    label: '계좌이체',
                    onClick: requestAccountPayment
                },
                //세금 계산서 요청, APi 연결 필요
                {
                    icon: <BanknotesIcon className={styles.icon}/>,
                    label: '세금계산서 요청',
                    onClick: openTaxPopUp
                },
                //결제방법 변경, 계좌이체 > 카드
                {
                    icon: <ArrowPathIcon className={styles.icon}/>,
                    label: '결제방법 변경',
                    onClick: changePaymentType
                },
                //작업자 추가 요청
                {
                    icon: <PlusIcon className={styles.icon}/>,
                    label: '작업자 추가',
                    onClick: addWorker
                }
            ];
        }

        //결제 방법이 신용카드인 경우
        if (paymentType === '신용카드') {
            return [
                //카드 결제
                {
                    icon: <CreditCardIcon className={styles.icon}/>,
                    label: '카드 결제',
                    onClick: requestCardPayment
                },
                //정기결제 카드 등록
                {
                    icon: <CreditCardIcon className={styles.icon}/>,
                    label: '등록 카드 변경',
                    onClick: registerBilling
                },
                //등록된 정기결제 카드 삭제
                {
                    icon: <CreditCardIcon className={styles.icon}/>,
                    label: '등록 카드 삭제',
                    onClick: deleteCard
                },
                //결제방법 변경, 카드 > 계좌이체
                {
                    icon: <ArrowPathIcon className={styles.icon}/>,
                    label: '결제방법 변경',
                    onClick: changePaymentType
                },
                //작업자 추가 요청
                {
                    icon: <PlusIcon className={styles.icon}/>,
                    label: '작업자 추가',
                    onClick: addWorker
                }
            ];
        }

        return commonButtons;
    };

    const buttons = getButtons();

    return (
        <div
            //결제 방법 별 버튼 출력
            className={`${styles.wrapper} ${paymentType === '신용카드' && card && card.cardNumber && card.company ? styles.hasCard : ''}`}>
            <div className={`${styles.container} ${paymentType === '신용카드' ? styles.isCard : ''}`}>
                {buttons.map((button, index) => (
                    <button
                        key={index}
                        className={styles.button}
                        onClick={button.onClick}
                    >
                        {button.icon}
                        <span className={styles.label}>{button.label}</span>
                    </button>
                ))}
            </div>
            {/*결제방법이 카드이고 정기결제 카드를 등록한 경우*/}
            {paymentType === '신용카드' && card && card.cardNumber && card.company && (
                //정기결제 카드 정보
                <TextCard
                    items={getCardInfo()}
                    className={styles.cardInfo}
                />
            )}
            {/*결제방법이 카드이고 정기결제 카드를 등록하지 않은 경우*/}
            {paymentType === '신용카드' && (!card || !card.cardNumber || !card.company) && (
                //안내 문구
                <div className={styles.noCardMessage}>
                    정기 결제를 위해<br className={styles.line}/> 카드를 등록해주세요
                </div>
            )}
            {/*첫 결제 방법 등록 팝업 페이지*/}
            <UpdatePaymentTypePopUp
                isOpen={isModalOpen}
                onClose={handleCloseModal}
            />
            {/*작업자 추가 요청 생성 팝업*/}
            <CreateWorkerRequestPopUp
                isOpen={isWorkerOpen}
                onClose={handleCloseWorkerModal}
            />
            <CreateTaxInvoicePopUp
                isOpen={isTaxInvoiceOpen}
                onClose={closeTaxPopUp}
            />
        </div>
    );
};

export default ClientBillingButtons;